import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Plus,
  Edit,
  Trash2,
  Calendar,
  Clock,
  Shield,
  Loader2,
} from 'lucide-react';
import { leaveApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

interface LeavePolicyFormData {
  name: string;
  leave_type: string;
  description: string;
  days_per_year: number;
  requires_approval: boolean;
  allow_carry_forward: boolean;
  max_carry_forward_days?: number;
  min_service_days?: number;
  is_paid: boolean;
  is_active: boolean;
}

const CustomLeaveTypes: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<any>(null);
  const [formData, setFormData] = useState<LeavePolicyFormData>({
    name: '',
    leave_type: 'CUSTOM',
    description: '',
    days_per_year: 10,
    requires_approval: true,
    allow_carry_forward: false,
    max_carry_forward_days: 0,
    min_service_days: 0,
    is_paid: true,
    is_active: true,
  });

  // Fetch leave policies
  const { data: leavePolicies = [], isLoading, error } = useQuery({
    queryKey: ['leavePolicies'],
    queryFn: () => leaveApi.policies.getAll(),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: leaveApi.policies.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leavePolicies'] });
      setIsCreateOpen(false);
      resetForm();
      toast({ title: 'Leave type created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => leaveApi.policies.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leavePolicies'] });
      setEditingPolicy(null);
      resetForm();
      toast({ title: 'Leave type updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: leaveApi.policies.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leavePolicies'] });
      toast({ title: 'Leave type deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleCreateLeaveType = () => {
    if (!formData.name || !formData.leave_type || !formData.description) {
      toast({ title: 'Error', description: 'Please fill required fields', variant: 'destructive' });
      return;
    }

    createMutation.mutate(formData);
  };

  const handleEditLeaveType = () => {
    if (!editingPolicy || !formData.name || !formData.leave_type || !formData.description) {
      toast({ title: 'Error', description: 'Please fill required fields', variant: 'destructive' });
      return;
    }

    updateMutation.mutate({ id: editingPolicy.id, data: formData });
  };

  const handleDeleteLeaveType = (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete "${name}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      leave_type: 'CUSTOM',
      description: '',
      days_per_year: 10,
      requires_approval: true,
      allow_carry_forward: false,
      max_carry_forward_days: 0,
      min_service_days: 0,
      is_paid: true,
      is_active: true,
    });
  };

  const openEditDialog = (policy: any) => {
    setEditingPolicy(policy);
    setFormData({
      name: policy.name,
      leave_type: policy.leave_type || 'CUSTOM',
      description: policy.description || '',
      days_per_year: policy.days_per_year || 10,
      requires_approval: policy.requires_approval ?? true,
      allow_carry_forward: policy.allow_carry_forward ?? false,
      max_carry_forward_days: policy.max_carry_forward_days || 0,
      min_service_days: policy.min_service_days || 0,
      is_paid: policy.is_paid ?? true,
      is_active: policy.is_active ?? true,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading leave types...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-lg">
        <p className="font-semibold">Error loading leave types</p>
        <p className="text-sm mt-1">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Custom Leave Types</h1>
          <p className="text-gray-600 mt-2">Configure and manage leave types for your organization</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="flex items-center space-x-2">
              <Plus className="h-4 w-4" />
              <span>Create Leave Type</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Leave Type</DialogTitle>
              <DialogDescription>
                Configure a new leave type with specific rules and eligibility criteria.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-6 py-4">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Basic Information</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Leave Type Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Study Leave"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="leave_type">Leave Type Category *</Label>
                    <Select
                      value={formData.leave_type}
                      onValueChange={(value) => setFormData({ ...formData, leave_type: value })}
                    >
                      <SelectTrigger id="leave_type">
                        <SelectValue placeholder="Select leave type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CUSTOM">Custom</SelectItem>
                        <SelectItem value="ANNUAL">Annual</SelectItem>
                        <SelectItem value="SICK">Sick</SelectItem>
                        <SelectItem value="PERSONAL">Personal</SelectItem>
                        <SelectItem value="MATERNITY">Maternity</SelectItem>
                        <SelectItem value="PATERNITY">Paternity</SelectItem>
                        <SelectItem value="UNPAID">Unpaid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Brief description of this leave type"
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Leave Rules */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Leave Rules</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="days_per_year">Days Per Year</Label>
                    <Input
                      id="days_per_year"
                      type="number"
                      value={formData.days_per_year}
                      onChange={(e) => setFormData({ ...formData, days_per_year: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="min_service_days">Min Service Days</Label>
                    <Input
                      id="min_service_days"
                      type="number"
                      value={formData.min_service_days}
                      onChange={(e) => setFormData({ ...formData, min_service_days: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
              </div>

              {/* Settings */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Settings</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Paid Leave</Label>
                    <Switch
                      checked={formData.is_paid}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_paid: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Requires Approval</Label>
                    <Switch
                      checked={formData.requires_approval}
                      onCheckedChange={(checked) => setFormData({ ...formData, requires_approval: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Allow Carry Forward</Label>
                    <Switch
                      checked={formData.allow_carry_forward}
                      onCheckedChange={(checked) => setFormData({ ...formData, allow_carry_forward: checked })}
                    />
                  </div>
                  {formData.allow_carry_forward && (
                    <div className="space-y-2 ml-6">
                      <Label htmlFor="max_carry_forward_days">Max Carry Forward Days</Label>
                      <Input
                        id="max_carry_forward_days"
                        type="number"
                        value={formData.max_carry_forward_days}
                        onChange={(e) => setFormData({ ...formData, max_carry_forward_days: parseInt(e.target.value) })}
                      />
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <Label>Active</Label>
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateLeaveType} disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating...' : 'Create Leave Type'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Leave Types Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {leavePolicies.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center text-muted-foreground">
              No leave types found. Create your first leave type.
            </CardContent>
          </Card>
        ) : (
          leavePolicies.map((policy: any) => (
            <Card key={policy.id} className="hover:shadow-lg transition-shadow cursor-pointer group">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{policy.name}</CardTitle>
                    <div className="flex gap-2 mt-2">
                      {policy.is_active ? (
                        <Badge variant="default">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                      {policy.is_paid && (
                        <Badge variant="outline" className="text-green-600 border-green-200">
                          Paid
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(policy)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteLeaveType(policy.id, policy.name)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600 text-sm">{policy.description || 'No description'}</p>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span>{policy.days_per_year || 0} days/year</span>
                  </div>
                  {policy.min_service_days > 0 && (
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span>{policy.min_service_days}d service</span>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  {policy.requires_approval && (
                    <Badge variant="outline" className="text-blue-600 border-blue-200">
                      <Shield className="h-3 w-3 mr-1" />
                      Approval
                    </Badge>
                  )}
                  {policy.allow_carry_forward && (
                    <Badge variant="outline" className="text-orange-600 border-orange-200">
                      Carry Forward
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingPolicy} onOpenChange={() => setEditingPolicy(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Leave Type</DialogTitle>
            <DialogDescription>
              Modify the configuration for this leave type.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Basic Information</h3>
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Leave Type Name *</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-leave_type">Leave Type Category *</Label>
                  <Select
                    value={formData.leave_type}
                    onValueChange={(value) => setFormData({ ...formData, leave_type: value })}
                  >
                    <SelectTrigger id="edit-leave_type">
                      <SelectValue placeholder="Select leave type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CUSTOM">Custom</SelectItem>
                      <SelectItem value="ANNUAL">Annual</SelectItem>
                      <SelectItem value="SICK">Sick</SelectItem>
                      <SelectItem value="PERSONAL">Personal</SelectItem>
                      <SelectItem value="MATERNITY">Maternity</SelectItem>
                      <SelectItem value="PATERNITY">Paternity</SelectItem>
                      <SelectItem value="UNPAID">Unpaid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description *</Label>
                  <Textarea
                    id="edit-description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Leave Rules */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Leave Rules</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-days_per_year">Days Per Year</Label>
                  <Input
                    id="edit-days_per_year"
                    type="number"
                    value={formData.days_per_year}
                    onChange={(e) => setFormData({ ...formData, days_per_year: parseInt(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-min_service_days">Min Service Days</Label>
                  <Input
                    id="edit-min_service_days"
                    type="number"
                    value={formData.min_service_days}
                    onChange={(e) => setFormData({ ...formData, min_service_days: parseInt(e.target.value) })}
                  />
                </div>
              </div>
            </div>

            {/* Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Settings</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Paid Leave</Label>
                  <Switch
                    checked={formData.is_paid}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_paid: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Requires Approval</Label>
                  <Switch
                    checked={formData.requires_approval}
                    onCheckedChange={(checked) => setFormData({ ...formData, requires_approval: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Allow Carry Forward</Label>
                  <Switch
                    checked={formData.allow_carry_forward}
                    onCheckedChange={(checked) => setFormData({ ...formData, allow_carry_forward: checked })}
                  />
                </div>
                {formData.allow_carry_forward && (
                  <div className="space-y-2 ml-6">
                    <Label htmlFor="edit-max_carry_forward_days">Max Carry Forward Days</Label>
                    <Input
                      id="edit-max_carry_forward_days"
                      type="number"
                      value={formData.max_carry_forward_days}
                      onChange={(e) => setFormData({ ...formData, max_carry_forward_days: parseInt(e.target.value) })}
                    />
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <Label>Active</Label>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPolicy(null)}>
              Cancel
            </Button>
            <Button onClick={handleEditLeaveType} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Updating...' : 'Update Leave Type'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomLeaveTypes;